import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  Divider,
  Paper,
  CircularProgress
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import { ApiConfigurations, PaymentScheduleSettings } from './Configurations';
import API from './Api';
import { useAppTheme } from '../../contexts/ThemeContext';

const CompanySettings = ({ showNotification }) => {
  const { palette, Colors } = useAppTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [companySettings, setCompanySettings] = useState({
    companyName: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    taxId: ''
  });

  const [originalSettings, setOriginalSettings] = useState({ ...companySettings });

  useEffect(() => {
    fetchCompanyProfile();
  }, []);

  const fetchCompanyProfile = async () => {
    try {
      setLoading(true);
      const response = await API.company.getCompanyProfile();
      setCompanySettings({
        companyName: response.company.name,
        address: response.company.address || '',
        phone: response.company.phone_number || '',
        email: response.company.email || '',
        website: response.company.website || '',
        taxId: response.company.code || ''
      });
      setError(null);
    } catch (err) {
      setError('Failed to load company profile');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCompanySettingsChange = (field) => (event) => {
    setCompanySettings({ ...companySettings, [field]: event.target.value });
  };

  const handleEdit = () => {
    setOriginalSettings({ ...companySettings });
    setIsEditing(true);
  };

  const handleSaveSettings = async () => {
    try {
      await API.company.updateCompanyProfile({
        name: companySettings.companyName,
        address: companySettings.address,
        phone_number: companySettings.phone,
        email: companySettings.email,
        website: companySettings.website,
      });
      setIsEditing(false);
      showNotification('Company settings saved successfully');
    } catch (err) {
      showNotification('Failed to save company settings', 'error');
      console.error(err);
    }
  };

  const handleCancel = () => {
    setCompanySettings({ ...originalSettings });
    setIsEditing(false);
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: palette.border },
      '&:hover fieldset': { borderColor: Colors.primary.main },
      backgroundColor: palette.background.secondary,
    },
    '& .MuiInputLabel-root': { color: palette.text.secondary },
    '& .MuiInputBase-input': { color: palette.text.primary },
    '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: palette.text.secondary },
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress sx={{ color: Colors.primary.main }} />
      </Box>
    );
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Box>
      {/* Company Details Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ color: Colors.primary.main }}>Company Details</Typography>
        {!isEditing && (
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            sx={{ borderColor: Colors.primary.main, color: Colors.primary.main }}
          >
            Edit
          </Button>
        )}
      </Box>

      <Paper
        elevation={isEditing ? 2 : 0}
        sx={{
          p: isEditing ? 3 : 0,
          mb: 4,
          backgroundColor: isEditing ? palette.background.tertiary : 'transparent',
          border: isEditing ? `1px solid ${palette.border}` : 'none',
        }}
      >
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Company Name" value={companySettings.companyName}
              onChange={handleCompanySettingsChange('companyName')} variant="outlined" margin="normal"
              disabled={!isEditing} InputProps={{ readOnly: !isEditing }} sx={fieldSx} />
            <TextField fullWidth label="Address" value={companySettings.address}
              onChange={handleCompanySettingsChange('address')} variant="outlined" margin="normal"
              multiline rows={2} disabled={!isEditing} InputProps={{ readOnly: !isEditing }} sx={fieldSx} />
            <TextField fullWidth label="Phone Number" value={companySettings.phone}
              onChange={handleCompanySettingsChange('phone')} variant="outlined" margin="normal"
              disabled={!isEditing} InputProps={{ readOnly: !isEditing }} sx={fieldSx} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Email Address" value={companySettings.email}
              onChange={handleCompanySettingsChange('email')} variant="outlined" margin="normal"
              disabled={!isEditing} InputProps={{ readOnly: !isEditing }} sx={fieldSx} />
            <TextField fullWidth label="Website" value={companySettings.website}
              onChange={handleCompanySettingsChange('website')} variant="outlined" margin="normal"
              disabled={!isEditing} InputProps={{ readOnly: !isEditing }} sx={fieldSx} />
            <TextField fullWidth label="Tax ID / EIN" value={companySettings.taxId}
              variant="outlined" margin="normal" disabled={true}
              InputProps={{ readOnly: true }}
              helperText="Tax ID cannot be edited directly"
              sx={{
                ...fieldSx,
                '& .MuiFormHelperText-root': { color: palette.text.muted },
              }} />
          </Grid>

          {isEditing && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2, borderColor: palette.border }} />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  sx={{ borderColor: palette.border, color: palette.text.secondary }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveSettings}
                  sx={{ bgcolor: Colors.primary.main, '&:hover': { bgcolor: Colors.primary.dark } }}
                >
                  Save Changes
                </Button>
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* API Configurations Section */}
      <Box sx={{ mt: 5 }}>
        <ApiConfigurations showNotification={showNotification} />
      </Box>

      {/* Payment Schedule Section */}
      <Box sx={{ mt: 5 }}>
        <PaymentScheduleSettings showNotification={showNotification} />
      </Box>
    </Box>
  );
};

export default CompanySettings;